import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Link } from '@tanstack/react-router';
import { Link as QuickLinks } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess } from '../../utils/helpers';
import { getIconButtonStyle } from '../../utils/styles';

interface QuickLinksTabProps {
  quickLinks?: QuickLinks[];
  userRole?: keyof typeof UserRole;
}

const QuickLinksTab: React.FC<QuickLinksTabProps> = ({ quickLinks = [], userRole }) => {
  const filteredLinks = useMemo(() => {
    if (!quickLinks.length) return [];

    return quickLinks.filter(
      (link) => checkRbacAccess({ accessibleByRoles: link.accessibleByRoles }, userRole).hasViewAccess
    );
  }, [quickLinks, userRole]);

  return (
    <div css={containerStyle}>
      {filteredLinks.length > 0 ? (
        filteredLinks.map((link) => (
          <div key={link.url} css={listItemStyle}>
            <div css={bulletStyle} />
            <div css={contentWrapperStyle}>
              <div css={headerStyle}>
                <Link to={link.url} target='_blank' rel='noopener noreferrer' css={linkStyle}>
                  {link.name}
                </Link>
                <div css={actionsStyle}>
                  <Button aria-label='Edit' css={getIconButtonStyle(theme.colors.black)}>
                    <Icon name='edit' size={20} />
                  </Button>
                  <Button aria-label='Remove' css={getIconButtonStyle(theme.colors.status.critical)}>
                    <Icon name='remove2' size={20} />
                  </Button>
                </div>
              </div>
              <Text css={descriptionStyle}>{link.description ?? 'Description ....'}</Text>
            </div>
          </div>
        ))
      ) : (
        <div css={emptyStateStyle}>
          <Text>No Quick Links available for your role.</Text>
        </div>
      )}

      <Button aria-label='Add New Link' iconName='add' variant='tertiary' size='large'>
        Add New Link
      </Button>
    </div>
  );
};

export default QuickLinksTab;

const containerStyle = css`
  border: 1px solid ${theme.colors.neutral[20]};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(3)};
  margin-top: ${theme.spacing(3)};
`;

const listItemStyle = css`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${theme.spacing(2)};
`;

const bulletStyle = css`
  &::before {
    content: '•';
    margin-right: ${theme.spacing(1)};
    color: ${theme.colors.primary[50]};
    vertical-align: sub;
  }
`;

const contentWrapperStyle = css`
  flex: 1;
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing(0.5)};
`;

const linkStyle = css`
  color: ${theme.colors.primary[50]};
  font-weight: ${theme.font.weight.bold};

  &:hover {
    color: ${theme.colors.primary[60]};
  }
`;

const actionsStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const descriptionStyle = css`
  color: ${theme.colors.neutral[60]};
  font-size: ${theme.font.size.small};
`;

const emptyStateStyle = css`
  padding-bottom: ${theme.spacing(2)};
  text-align: left;
  color: ${theme.colors.neutral[50]};
`;
